import { Badge, Button, CardDownloadFile, Drawer } from '@iveco-ti/iveco-ds'
import DOMPurify from 'dompurify'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import DefaultIMG from '@/assets/images/post-default.svg'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { formatDate } from '@/common/utils/formatDate'
import { useDownloadNewsAttachment } from '../hooks/useDownloadNewsAttachment'
import { useViewNews } from '../hooks/useViewNews'

export function DrawerViewNews() {
  const { t } = useTranslation('panel')
  const { news, closeNews } = useViewNews()
  const { userInfo } = useGetUserInfo()
  const { handleDownloadNewsAttachment } = useDownloadNewsAttachment()

  return (
    <Drawer.Root id="drawer-view-news">
      <Drawer.Header
        className="flex items-center justify-between gap-4 mr-4 w-full"
        onClose={closeNews}
      >
        <div>
          <h2 className="text-lg font-semibold">
            {t('news.drawer_view_news.header.title')}
          </h2>

          {news?.updatedAt && (
            <p className="text-xs text-base-600">
              {t('news.drawer_view_news.header.updated_at', {
                date: formatDate(new Date(news.updatedAt), 'PP'),
              })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {news?.tag?.map((tag) => (
            <Badge.Root key={tag} shape="square" size="sm">
              {tag}
            </Badge.Root>
          ))}
        </div>
      </Drawer.Header>

      <Drawer.Body>
        <div className="flex flex-col h-full ">
          <div className="flex items-start w-full justify-between">
            <div className="space-y-2">
              {news?.title && (
                <h1 className="font-semibold text-2xl text-base-900">
                  {news.title}
                </h1>
              )}

              {news?.subTitle && (
                <h2 className="text-base-800 text-base font-semibold">
                  {news.subTitle}
                </h2>
              )}
            </div>

            {news?.postExternalUrl && (
              <a
                href={`https://${news.postExternalUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="text-brand-600 size-5 hover:text-brand-700" />
              </a>
            )}
          </div>

          <img
            src={news?.postImage || DefaultIMG}
            alt=""
            className="rounded mt-4 max-h-52 w-full object-cover object-center"
          />

          {news?.post?.content && (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(news.post.content),
              }}
              className="no-reset mt-4 flex-1"
            />
          )}

          {news?.postFile && (
            <div className="pb-4 mt-auto">
              <CardDownloadFile.Card
                fileName={news?.postFile.content}
                fileSize={news?.postFile.fileSize}
                onDownload={() =>
                  handleDownloadNewsAttachment({
                    newsId: news.id,
                    fileName: news?.postFile?.content,
                  })
                }
              />
            </div>
          )}
        </div>
      </Drawer.Body>

      <Drawer.Footer className="w-full flex justify-between items-center">
        {userInfo?.UserPreference?.allowedPosts && (
          <Button.Root color="danger" variant="secondary">
            {t('news.drawer_view_news.buttons.delete')}
          </Button.Root>
        )}

        <Button.Root variant="secondary" onClick={closeNews}>
          {t('news.drawer_view_news.buttons.close')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
